import { useEffect } from "react";
import "@/App.css";
import { initTheme } from "@/components/ThemeToggle";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/tac";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import AuthPage from "@/pages/AuthPage";
import Portal from "@/pages/Portal";
import PaymentResult from "@/pages/PaymentResult";
import AppLayout from "@/components/AppLayout";
import Dispatch from "@/pages/app/Dispatch";
import Jobs from "@/pages/app/Jobs";
import NewJobWizard from "@/pages/app/NewJobWizard";
import JobDetail from "@/pages/app/JobDetail";
import Customers from "@/pages/app/Customers";
import Review from "@/pages/app/Review";
import TechToday from "@/pages/app/TechToday";
import Billing from "@/pages/app/Billing";
import Team from "@/pages/app/Team";
import Fleet from "@/pages/app/Fleet";
import Notifications from "@/pages/app/Notifications";
import Templates from "@/pages/app/Templates";
import Compliance from "@/pages/app/Compliance";
import Projects from "@/pages/app/Projects";
import ProjectDetail from "@/pages/app/ProjectDetail";
import AccountSettings from "@/pages/app/AccountSettings";
import Pricebook from "@/pages/app/Pricebook";
import Vendors from "@/pages/app/Vendors";
import SubDashboard from "@/pages/app/SubDashboard";
import Payouts from "@/pages/app/Payouts";
import Inventory from "@/pages/app/Inventory";
import Assets from "@/pages/app/Assets";
import Feed from "@/pages/app/Feed";
import PublicTimeline from "@/pages/PublicTimeline";
import Quotes from "@/pages/app/Quotes";
import QuoteDetail from "@/pages/app/QuoteDetail";
import PublicQuote from "@/pages/PublicQuote";
import Industries from "@/pages/Industries";
import ContactInbox from "@/pages/app/ContactInbox";
import Permits from "@/pages/app/Permits";
import PropertyReminders from "@/pages/app/PropertyReminders";
import Schedule from "@/pages/app/Schedule";
import PublicBooking from "@/pages/PublicBooking";
import PublicAppt from "@/pages/PublicAppt";
import PublicReview from "@/pages/PublicReview";
import ReviewsAdmin from "@/pages/app/ReviewsAdmin";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0F17]">
      <Spinner label="Establishing session" />
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  useEffect(() => { initTheme(); }, []);
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user && user.role === "technician") return <Navigate to="/tech/today" replace />;
  return <Navigate to="/dispatch" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Legal page="privacy" />} />
      <Route path="/terms" element={<Legal page="terms" />} />
      <Route path="/refund-policy" element={<Legal page="refund-policy" />} />
      <Route path="/eula" element={<Legal page="eula" />} />
      <Route path="/security" element={<Legal page="security" />} />
      <Route path="/dpa" element={<Legal page="dpa" />} />
      <Route path="/contact" element={<Legal page="contact" />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/q/:token" element={<PublicQuote />} />
      <Route path="/t/:token" element={<PublicTimeline />} />
      <Route path="/book/:workspaceId" element={<PublicBooking />} />
      <Route path="/appt/:token" element={<PublicAppt />} />
      <Route path="/review/:workspaceId" element={<PublicReview />} />
      <Route path="/industries/:slug" element={<Industries />} />
      <Route path="/payment/success" element={<PaymentResult />} />

      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/home" element={<HomeRedirect />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/new" element={<NewJobWizard />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/reviews" element={<ReviewsAdmin />} />
        <Route path="/review" element={<Review />} />
        <Route path="/tech/today" element={<TechToday />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/team" element={<Team />} />
        <Route path="/fleet" element={<Fleet />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/quotes/:id" element={<QuoteDetail />} />
        <Route path="/pricebook" element={<Pricebook />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/sub" element={<SubDashboard />} />
        <Route path="/payouts" element={<Payouts />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/assets" element={<Assets />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/leads" element={<ContactInbox />} />
        <Route path="/permits" element={<Permits />} />
        <Route path="/reminders" element={<PropertyReminders />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/account" element={<AccountSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  return (
    <HelmetProvider>
      <div className="App">
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <CookieBanner />
            <Toaster theme="dark" position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
